<template>
  <div class="rich-text-editor">
    <div class="rich-text-editor__toolbar" aria-label="Text formatting">
      <button
        v-for="action in visibleFormatActions"
        :key="action.command"
        type="button"
        class="rich-text-editor__tool"
        :title="action.label"
        @mousedown.prevent
        @click="runCommand(action.command, action.value)"
      >
        <i :class="action.icon"></i>
      </button>
      <button
        v-if="showLinks"
        type="button"
        class="rich-text-editor__tool"
        title="Add link"
        @mousedown.prevent
        @click="addLink"
      >
        <i class="ri-link"></i>
      </button>
      <button
        v-if="showLinks"
        type="button"
        class="rich-text-editor__tool"
        title="Remove link"
        @mousedown.prevent
        @click="runCommand('unlink')"
      >
        <i class="ri-link-unlink"></i>
      </button>
    </div>

    <div
      ref="editorRef"
      class="rich-text-editor__surface"
      :class="{ 'rich-text-editor__surface--empty': isEmpty }"
      :contenteditable="!disabled"
      :data-placeholder="placeholder"
      role="textbox"
      aria-multiline="true"
      @input="handleInput"
      @blur="handleBlur"
      @paste.prevent="handlePaste"
    ></div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showHeading: {
    type: Boolean,
    default: true
  },
  showLinks: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const editorRef = ref(null)
const lastValue = ref('')

const formatActions = [
  { command: 'formatBlock', value: 'p', label: 'Paragraph', icon: 'ri-text' },
  { command: 'formatBlock', value: 'h3', label: 'Heading', icon: 'ri-heading' },
  { command: 'bold', label: 'Bold', icon: 'ri-bold' },
  { command: 'italic', label: 'Italic', icon: 'ri-italic' },
  { command: 'underline', label: 'Underline', icon: 'ri-underline' },
  { command: 'insertUnorderedList', label: 'Bullet list', icon: 'ri-list-unordered' },
  { command: 'insertOrderedList', label: 'Numbered list', icon: 'ri-list-ordered' }
]

const visibleFormatActions = computed(() => (
  props.showHeading
    ? formatActions
    : formatActions.filter((action) => action.command !== 'formatBlock' || action.value !== 'h3')
))

const allowedTags = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'U', 'P', 'BR', 'UL', 'OL', 'LI', 'H3'])

const isEmpty = computed(() => !stripHtml(lastValue.value).trim())

const stripHtml = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return container.textContent || ''
}

const sanitizeNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent || '')
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return document.createTextNode('')
  }

  const tagName = node.tagName.toUpperCase()
  const nextTag = allowedTags.has(tagName) ? tagName.toLowerCase() : 'span'
  const nextNode = document.createElement(nextTag)

  if (tagName === 'A') {
    const href = normalizeUrl(node.getAttribute('href') || '')
    if (href) {
      nextNode.setAttribute('href', href)
      nextNode.setAttribute('target', '_blank')
      nextNode.setAttribute('rel', 'noopener noreferrer')
    }
  }

  Array.from(node.childNodes).forEach((child) => {
    nextNode.appendChild(sanitizeNode(child))
  })

  if (nextTag === 'span') {
    const fragment = document.createDocumentFragment()
    Array.from(nextNode.childNodes).forEach((child) => fragment.appendChild(child))
    return fragment
  }

  return nextNode
}

const sanitizeHtml = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')

  const clean = document.createElement('div')
  Array.from(container.childNodes).forEach((node) => {
    clean.appendChild(sanitizeNode(node))
  })

  return clean.innerHTML
}

const normalizeUrl = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const candidate = /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`

  try {
    const url = new URL(candidate)
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) return ''
    return url.href
  } catch {
    return ''
  }
}

const setEditorHtml = (value) => {
  const editor = editorRef.value
  if (!editor) return
  const nextValue = sanitizeHtml(value)
  if (editor.innerHTML !== nextValue) {
    editor.innerHTML = nextValue
  }
  lastValue.value = nextValue
}

const emitValue = () => {
  const editor = editorRef.value
  if (!editor) return
  const nextValue = sanitizeHtml(editor.innerHTML)
  lastValue.value = nextValue
  emit('update:modelValue', nextValue)
}

const runCommand = (command, value = null) => {
  if (props.disabled) return
  editorRef.value?.focus()
  document.execCommand(command, false, value)
  emitValue()
}

const getSelectionText = () => {
  const selection = window.getSelection()
  if (!selection || !editorRef.value?.contains(selection.anchorNode)) return ''
  return selection.toString().trim()
}

const addLink = () => {
  if (props.disabled) return
  editorRef.value?.focus()
  const url = normalizeUrl(window.prompt('Enter link URL') || '')
  if (!url) return

  const selectionText = getSelectionText()
  const displayText = String(window.prompt('Enter link text', selectionText || url) || '').trim()
  if (!displayText) return

  if (selectionText) {
    document.execCommand('createLink', false, url)
    const selection = window.getSelection()
    const anchor = selection?.anchorNode?.parentElement?.closest?.('a')
    if (anchor && editorRef.value?.contains(anchor)) {
      anchor.textContent = displayText
    }
  } else {
    const anchorHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${escapeHtml(displayText)}</a>`
    document.execCommand('insertHTML', false, anchorHtml)
  }

  emitValue()
}

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const handleInput = () => {
  emitValue()
}

const handleBlur = () => {
  setEditorHtml(editorRef.value?.innerHTML || '')
  emit('update:modelValue', lastValue.value)
}

const handlePaste = (event) => {
  const text = event.clipboardData?.getData('text/plain') || ''
  document.execCommand('insertText', false, text)
  emitValue()
}

watch(
  () => props.modelValue,
  (value) => {
    if (sanitizeHtml(value) === lastValue.value) return
    nextTick(() => setEditorHtml(value))
  },
  { immediate: true }
)
</script>

<style scoped>
.rich-text-editor {
  margin-top: 0.5rem;
  overflow: hidden;
  border: 1px solid #e8ddd1;
  border-radius: 1rem;
  background: #fff;
}

.rich-text-editor:focus-within {
  border-color: #c73d3d;
}

.rich-text-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  border-bottom: 1px solid #f0e5da;
  background: #fffaf5;
  padding: 0.45rem;
}

.rich-text-editor__tool {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: #5b4638;
  font-size: 1rem;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.rich-text-editor__tool:hover {
  border-color: #e5d8cc;
  background: #fff;
  color: #c73d3d;
}

.rich-text-editor__surface {
  min-height: 8.5rem;
  padding: 0.9rem 1rem;
  color: #251833;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.65;
  outline: none;
  text-transform: none;
}

.rich-text-editor__surface--empty::before {
  content: attr(data-placeholder);
  pointer-events: none;
  color: #9b8b7e;
}

.rich-text-editor__surface :deep(h3) {
  margin: 0.5rem 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.4;
}

.rich-text-editor__surface :deep(p) {
  margin: 0.35rem 0;
}

.rich-text-editor__surface :deep(ul),
.rich-text-editor__surface :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  list-style-position: outside;
}

.rich-text-editor__surface :deep(ul) {
  list-style-type: disc;
}

.rich-text-editor__surface :deep(ol) {
  list-style-type: decimal;
}

.rich-text-editor__surface :deep(li) {
  display: list-item;
  margin: 0.2rem 0;
}

.rich-text-editor__surface :deep(a) {
  color: #c73d3d;
  font-weight: 600;
  text-decoration: underline;
}
</style>
